/* eslint-disable react/jsx-props-no-spreading */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Col, Form, Row, Table } from 'react-bootstrap';
import { BsCheck, BsX } from 'react-icons/bs';
import { useSearchParams } from 'react-router-dom';
import {
  useTable,
  useExpanded,
  useFilters,
  usePagination,
  useRowSelect,
} from 'react-table';
// eslint-disable-next-line import/no-unresolved
import { toast } from 'react-toastify';

import classNames from 'classnames';
import { useFormikContext } from 'formik';

import { updateQueryStringOnFieldChange } from '@components/Filters/Filters';
import { DefaultSpinnerCenter } from '@components/Loader/Loader';
import {
  BootstrapItemsPerPage,
  BootstrapPagination,
} from '@components/Table/Pagination';
import { HTTP_400_BAD_REQUEST, HTTP_500_SERVER_ERROR } from '@config/const';
import { Page500 } from '@containers/ErrorPages';
import { removeEmpty } from '@features/removeEmpty';
import { useThrottle } from '@features/useThrottle';
import { useHasChanged } from '@features/utils';
import { skipToken } from '@reduxjs/toolkit/query';

export const renderBooleanValue = ({ value }) =>
  value === true ? <BsCheck size={27} /> : <BsX size={27} />;

export const renderFilterHeader = ({ fieldName, header, useContext }) => {
  const { handleChange, handleBlur, values } = useContext();
  const [searchParams, setSearchParams] = useSearchParams();

  const throttle = useThrottle((event) => {
    updateQueryStringOnFieldChange(event, { searchParams, setSearchParams });
  }, 500);

  const throttledChangeHandler = useCallback(
    (event) => {
      handleChange(event);
      throttle(event);
    },
    [throttle]
  );
  return (
    <Form.Group controlId={`formGroup${fieldName}`}>
      <Form.Label style={{ textTransform: 'capitalize' }}>
        {header || fieldName}
      </Form.Label>
      <Form.Control
        type="input"
        name={fieldName}
        size="sm"
        value={values[fieldName]}
        onChange={throttledChangeHandler}
        onBlur={handleBlur}
        placeholder="Search"
      />
    </Form.Group>
  );
};

export const renderFormikFilterHeader = ({ fieldName, header }) =>
  renderFilterHeader({ fieldName, header, useContext: useFormikContext });

const defaultColumnOptions = {
  Cell: ({ cell }) =>
    typeof cell.value === 'boolean'
      ? renderBooleanValue({ value: cell.value })
      : cell.value,
};

function createColumnStyle(column) {
  const style = { ...column.style };
  if (column.setMinWidth) {
    style.minWidth = column.setMinWidth;
  }
  if (column.setMaxWidth) {
    style.maxWidth = column.setMaxWidth;
  }
  return style;
}

function getTableRows({ tableInstance, paginated }) {
  let tableRows;
  const { rows } = tableInstance;
  tableRows = rows;
  if (paginated) {
    const { page } = tableInstance;
    tableRows = page;
  }
  return tableRows;
}

function getTableHeaderRows({ tableInstance, hideNested }) {
  let tableHeaderRows;
  const { headerGroups } = tableInstance;
  tableHeaderRows = headerGroups;
  if (hideNested) {
    tableHeaderRows = headerGroups.filter((headerGroup) => {
      if (!headerGroup.headers) {
        return false;
      }
      return headerGroup.headers
        .map((header) => header.depth === 0)
        .every((item) => item === true);
    });
  }
  return tableHeaderRows;
}

const TableHeaders = ({ columns, headerRows, headerProps, hide = false }) =>
  !hide
    ? headerRows.map((headerGroup) => (
        <tr {...headerGroup.getHeaderGroupProps()}>
          {headerGroup.headers.map((column) => {
            const { className, ...headerRestProps } = headerProps(columns);
            return (
              <th
                {...column.getHeaderProps({
                  className: classNames(column.className, className),
                  style: createColumnStyle(column),
                  ...headerRestProps,
                })}
              >
                {column.render('Header')}
              </th>
            );
          })}
        </tr>
      ))
    : null;

const TableRows = ({ rows, prepareRow, rowProps, cellProps }) =>
  rows.map((row) => {
    prepareRow(row);
    const { className, ...rowRestProps } = rowProps(row);
    return (
      <tr
        {...row.getRowProps({
          onClick: () => (row.canExpand ? row.toggleRowExpanded() : null),
          className: classNames(className, {
            rowCanExpand: row.canExpand,
          }),
          ...rowRestProps,
        })}
      >
        {row.cells.map((cell) => {
          const renamedCellProps = {
            ...cellProps(cell),
            cellClassName: cellProps(cell).className,
          };
          delete renamedCellProps.className;
          const { cellClassName, ...cellRestProps } = cellProps(cell);
          return (
            <td
              {...cell.getCellProps({
                className: classNames(cell.column.className, cellClassName),
                ...cellRestProps,
              })}
            >
              {cell.render('Cell')}
            </td>
          );
        })}
      </tr>
    );
  });

const NoResultsMessage = ({ emptyMessageContent = () => null }) =>
  emptyMessageContent();

const CountSelectedRows = ({ tableInstance }) => {
  const {
    state: { selectedRowIds },
  } = tableInstance;
  const selectedRowsLength = Object.keys(selectedRowIds).length;
  const variant = selectedRowsLength === 1 ? 'object' : 'objects';
  return (
    <p className="normalFontSize">
      Selected <b>{selectedRowsLength}</b> {variant}.
    </p>
  );
};

const itemsPerPage = [20, 50, 100, 250];

export const BootstrapTable = ({
  columns,
  data,
  filterData = {},
  tableProps = () => ({}),
  headerProps = () => ({}),
  rowProps = () => ({}),
  cellProps = () => ({}),
  tableHeader = null,
  hideHeader = false,
  hideNestedHeader = false,
  hiddenColumns = [],
  paginated = false,
  exportContent = () => null,
  selectedRows = false,
  isLoading = false,
  emptyMessageContent = () => null,
}) => {
  const defaultColumn = useMemo(() => ({ ...defaultColumnOptions }), []);
  const getSubRows = useCallback((row) => row?.others || [], []);
  let tableOptions = {
    columns,
    data,
    defaultColumn,
    getSubRows,
    initialState: {
      hiddenColumns,
    },
  };
  let tableHooks = [useExpanded, useFilters];
  if (paginated) {
    tableOptions = {
      ...tableOptions,
      autoResetSortBy: false,
      autoResetExpanded: false,
      autoResetPage: false,
      initialState: {
        ...tableOptions.initialState,
        pageIndex: 0,
        pageSize: itemsPerPage[0],
      },
    };
    tableHooks = [...tableHooks, usePagination];
  }
  if (selectedRows) {
    tableHooks = [...tableHooks, useRowSelect];
  }
  const tableInstance = useTable({ ...tableOptions }, ...tableHooks);
  const { getTableProps, prepareRow, setFilter } = tableInstance;

  useEffect(() => {
    Object.entries(filterData).map((item) => {
      const field = item[0];
      const value = item[1];

      return value !== null ? setFilter(field, value) : null;
    });
  }, [filterData]);

  const rows = getTableRows({ tableInstance, paginated });
  const headerRows = getTableHeaderRows({
    tableInstance,
    hideNested: hideNestedHeader,
  });
  return isLoading ? (
    <DefaultSpinnerCenter />
  ) : (
    <>
      {tableHeader ? <div className="mb-2">{tableHeader}</div> : null}
      <Table {...getTableProps(tableProps())}>
        <thead>
          <TableHeaders
            columns={columns}
            headerRows={headerRows}
            headerProps={headerProps}
            hide={hideHeader}
          />
        </thead>
        <tbody>
          <TableRows
            rows={rows}
            prepareRow={prepareRow}
            rowProps={rowProps}
            cellProps={cellProps}
          />
        </tbody>
      </Table>
      {rows.length === 0 ? (
        <NoResultsMessage emptyMessageContent={emptyMessageContent} />
      ) : null}
      {selectedRows ? (
        <CountSelectedRows tableInstance={tableInstance} />
      ) : null}
      {paginated ? (
        <>
          <Row className="mt-3">
            <Col>{exportContent()}</Col>
            <Col>
              <BootstrapItemsPerPage
                tableInstance={tableInstance}
                itemsPerPage={itemsPerPage}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <BootstrapPagination tableInstance={tableInstance} />
            </Col>
          </Row>
        </>
      ) : (
        exportContent()
      )}
    </>
  );
};

export const BootstrapGenericTable = ({
  columns,
  useData,
  useDataArgs = {},
  filterData = {},
  tableProps = () => ({}),
  headerProps = () => ({}),
  rowProps = () => ({}),
  cellProps = () => ({}),
  tableHeader = null,
  hideHeader = false,
  hideNestedHeader = false,
  hiddenColumns = [],
  paginated = false,
  exportContent = () => null,
  selectedRows = false,
  getRowId = (row) => row?.id,
  emptyMessageContent = () => null,
}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [, setTotal] = useState(0);
  const [controlledPageCount, setPageCount] = useState(0);

  let useDataProps =
    Object.keys(filterData).length > 0
      ? { ...removeEmpty(filterData), ...useDataArgs }
      : { ...useDataArgs };
  const defaultColumn = useMemo(() => ({ ...defaultColumnOptions }), []);
  const getSubRows = useCallback((row) => row.others || [], []);
  let tableOptions = {
    columns,
    data,
    defaultColumn,
    manualFilters: true,
    getSubRows,
    initialState: {
      hiddenColumns,
    },
  };
  let tableHooks = [useExpanded, useFilters];
  if (paginated) {
    tableOptions = {
      ...tableOptions,
      manualPagination: true,
      autoResetPage: false,
      pageCount: controlledPageCount,
      initialState: {
        ...tableOptions.initialState,
        pageIndex: 0,
        pageSize: itemsPerPage[0],
      },
    };
    tableHooks = [...tableHooks, usePagination];
  }
  if (selectedRows) {
    tableOptions = {
      ...tableOptions,
      autoResetSelectedRows: false,
    };
    tableHooks = [...tableHooks, useRowSelect];
    if (getRowId() !== null) {
      tableOptions = {
        ...tableOptions,
        getRowId,
      };
    }
  }
  const tableInstance = useTable({ ...tableOptions }, ...tableHooks);
  const {
    getTableProps,
    prepareRow,
    state: { pageIndex, pageSize },
  } = tableInstance;
  if (paginated) {
    useDataProps = { ...useDataProps, page: pageIndex + 1, size: pageSize };
  }
  const {
    data: result,
    isFetching,
    isLoading,
    isError,
    error,
  } = useData(
    useDataArgs !== skipToken
      ? {
          ...useDataProps,
        }
      : skipToken
  );
  const hasFilterDataChanged = useHasChanged(filterData);
  useEffect(() => {
    if (hasFilterDataChanged && paginated) {
      const { gotoPage } = tableInstance;
      gotoPage(0);
      setPageCount(0);
    }
    const results = result?.results || [];
    const count = result?.count || 0;
    setLoading(isLoading || isFetching);
    setData(!(isLoading || isFetching) && !isError ? results : []);
    setTotal(!(isLoading || isFetching) && count);
    setPageCount(
      !(isLoading || isFetching) && count ? Math.ceil(count / pageSize) : 0
    );
  }, [isLoading, isFetching, result, pageIndex, pageSize, loading, filterData]);

  useEffect(() => {
    if (isError && error.status === HTTP_400_BAD_REQUEST) {
      setData([]);
      toast.error('Filtering data failed.', { autoClose: 2500 });
    }
  }, [isError, error]);

  const rows = getTableRows({ tableInstance, paginated });
  const headerRows = getTableHeaderRows({
    tableInstance,
    hideNested: hideNestedHeader,
  });

  return (
    <>
      {tableHeader ? <div className="mb-2">{tableHeader}</div> : null}
      <Table {...getTableProps(tableProps())}>
        <thead>
          <TableHeaders
            columns={columns}
            headerRows={headerRows}
            headerProps={headerProps}
            hide={hideHeader}
          />
        </thead>
        <tbody>
          {!loading ? (
            <TableRows
              rows={rows}
              prepareRow={prepareRow}
              rowProps={rowProps}
              cellProps={cellProps}
            />
          ) : null}
        </tbody>
      </Table>
      {!loading && rows.length === 0 ? (
        <NoResultsMessage emptyMessageContent={emptyMessageContent} />
      ) : null}
      {!loading && isError && error.status === HTTP_500_SERVER_ERROR ? (
        <Page500 />
      ) : null}
      {loading ? <DefaultSpinnerCenter /> : null}
      {selectedRows ? (
        <CountSelectedRows tableInstance={tableInstance} />
      ) : null}
      {paginated ? (
        <>
          <Row className="mt-3">
            <Col>{exportContent(tableInstance)}</Col>
            <Col>
              <BootstrapItemsPerPage
                tableInstance={tableInstance}
                itemsPerPage={itemsPerPage}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <BootstrapPagination tableInstance={tableInstance} />
            </Col>
          </Row>
        </>
      ) : (
        exportContent()
      )}
    </>
  );
};

export const BootstrapVerticalColumnTable = ({
  columns,
  data,
  tableProps = () => ({}),
  headerProps = () => ({}),
  valueProps = () => ({}),
  tableHeader = null,
  hiddenColumns = [],
  isLoading = false,
}) => {
  const getSubRows = useCallback((row) => row?.others || [], []);
  const defaultColumn = useMemo(() => ({ ...defaultColumnOptions }), []);
  const { getTableProps, headerGroups, rows, prepareRow } = useTable(
    {
      columns,
      data,
      defaultColumn,
      getSubRows,
      initialState: {
        hiddenColumns,
      },
    },
    useExpanded
  );

  function renderVerticalHeader() {
    const endData = [];
    const preparedRows = rows.map((row) => {
      prepareRow(row);
      return row.cells.map((cell) => cell.render('Cell'));
    });
    headerGroups.map((headerGroup) =>
      headerGroup.headers.map((column, index) =>
        endData.push({
          header: column,
          cell: preparedRows[0][index].props.cell,
        })
      )
    );
    return endData.map((item, index) => {
      const headerValue = item.header.render('Header');
      const cellValue = item.cell.render('Cell');
      return (
        // eslint-disable-next-line react/no-array-index-key
        <tr key={item.header.id + cellValue + index}>
          <th
            className={classNames(
              item.cell.column.className,
              headerProps(item.row).className
            )}
          >
            {headerValue}
          </th>
          <td
            className={classNames(
              item.cell.column.className,
              valueProps(item.row).className
            )}
          >
            {cellValue}
          </td>
        </tr>
      );
    });
  }

  return isLoading ? (
    <DefaultSpinnerCenter />
  ) : (
    <>
      {tableHeader ? <div className="mb-2">{tableHeader}</div> : null}
      <Table {...getTableProps(tableProps())}>
        <tbody>{renderVerticalHeader()}</tbody>
      </Table>
    </>
  );
};

export function useTableRowsSelection() {
  const [selectedRows, setSelectedRows] = useState([]);
  const onRowSelectionChange = useCallback(
    (row) => (e) => {
      row.toggleRowSelected(e.target.checked);
      if (!row.isSelected) {
        setSelectedRows((prev) => [...prev, row.original]);
      } else {
        setSelectedRows((prev) => prev.filter((item) => item !== row.original));
      }
    },
    []
  );

  const onAllRowsSelectionChange = useCallback(
    (table) => (e) => {
      table.toggleAllRowsSelected(e.target.checked);
      const values = table.page.map((row) => row.original);
      if (table.isAllRowsSelected) {
        const excludedValues = new Set(values);
        setSelectedRows((prev) =>
          prev.filter((item) => !excludedValues.has(item))
        );
      } else {
        setSelectedRows((prev) => [...prev, ...values]);
      }
    },
    []
  );
  const clearAllRowsSelection = useCallback(
    (table) => () => {
      table.toggleAllRowsSelected(false);
      setSelectedRows([]);
    },
    []
  );

  return {
    selectedRows,
    clearAllRowsSelection,
    onRowSelectionChange,
    onAllRowsSelectionChange,
  };
}
